import { Box, Container, Flex, Icon, Image, Modal } from '@energiebespaarders/symbols';
import { Heading } from '@energiebespaarders/symbols/helpers';
import { Heart } from '@energiebespaarders/symbols/icons/solid';
import Head from 'next/head';
import Router from 'next/router';
import { useState } from 'react';
import { Element as Anchor } from 'react-scroll';
import Benefits from '~/components/HomePage/Benefits';
import Hero from '~/components/HomePage/Hero';
import Process from '~/components/HomePage/Process';
import TestimonialSlider from '~/components/HomePage/TestimonialSlider';
import Truster from '~/components/HomePage/Truster';
import Expectations from '~/components/PartnerLanding/Expectations';
import config from '~/config';
import { getPathnameFromAsPath } from '~/hooks/usePathname';
import type { Page } from '~/types/next';
import { DEB_PARTNERS } from '~/utils/debPartners';
import theme from '../../styles/theme';
import { PartnerKey, partnerLandingRoutes } from '../../utils/constants';
import DEBLogo from '/public/img/logo_WEB.svg';

interface PartnerLandingProps {
  partnerKey?: PartnerKey;
}

/**
 * Template for landing pages for acquisition partners WITHOUT request form.
 * Looks more like homepage, CTA is mostly aimed at Bespaarcheck.
 * Used by:
 * - /florius
 * - /gavoora
 * - /huis-hypotheek
 * - /hypokeur
 * - /hypotheekberekenen
 * - /hypotheekshop
 * - /nbg
 * - /triodos
 * - /viisi
 *
 */
const PartnerLanding: Page<PartnerLandingProps> = ({ partnerKey }) => {
  const [videoId, setVideoId] = useState('');

  if (!partnerKey) return <></>;

  const partner = DEB_PARTNERS.find(partner => partner.key === partnerKey);
  if (!partner) return <></>;

  const pageTitle = `Energiebespaarders ⤫ ${partner.name}`;
  const pageDescription = `Energiebespaarders is officieel partner van ${partner.name} voor duurzaam wonen.`;
  const hideExpectations = partner.key === PartnerKey.GavoorA;
  return (
    <>
      <Head>
        <title key="title">{pageTitle}</title>
        <meta name="description" key="meta-description" content={pageDescription} />
        <meta property="og:title" key="meta-og:title" content={pageTitle} />
        <meta property="og:description" key="meta-og:description" content={pageDescription} />

        <link
          key="link-canonical"
          rel="canonical"
          href={`${config.urls.base}/${partner.key.toLowerCase()}`}
        />
      </Head>

      <Hero
        partner={partner.key}
        title={`Samen met ${partner.name} maken wij duurzaam wonen eenvoudig en bereikbaar.`}
      />

      <Truster singlePartner={partner.key} />

      <Process />

      <Anchor name="moreInfo">
        <Flex style={{ position: 'relative', background: theme.colors.grayLighter }} py={5}>
          <Box width={1}>
            <Container size="lg">
              <Flex flexWrap="wrap" justifyContent="center" px={[3, 3, 0]}>
                <Box width={1}>
                  <Heading heading="h3">
                    Officieel partner van {partner.name} voor duurzaam wonen
                  </Heading>
                  <p>
                    Bij {partner.name} en Energiebespaarders sta jij als klant centraal. Door onze
                    samenwerking willen we je zoveel mogelijk inzicht geven in welke maatregelen je
                    kunt nemen om energie te besparen. Zo weet je snel en concreet welke
                    energiebesparende maatregelen je kunt treffen, hoeveel ze je kosten en wat ze je
                    uiteindelijk opleveren.
                  </p>
                  {!hideExpectations && <Expectations />}
                  <Flex alignItems="center" justifypartner="space-around" py={5}>
                    <Box width={1} textAlign="right">
                      <div>
                        <Image src={DEBLogo} alt="Energiebespaarders logo" width="180px" />
                      </div>
                    </Box>
                    <Box width={1 / 4} textAlign="center">
                      <Icon icon={Heart} solid fill="green" mx={3} />
                    </Box>
                    <Box width={1} textAlign="left">
                      <div>
                        <Image
                          src={partner.logo}
                          alt={`${partner.name} logo`}
                          width={partner.logoWidth}
                          style={partner.logoHeight ? { maxHeight: partner.logoHeight } : {}}
                        />
                      </div>
                    </Box>
                  </Flex>
                </Box>
              </Flex>
            </Container>
          </Box>
        </Flex>
      </Anchor>

      <TestimonialSlider />
      <Benefits />

      <Modal
        contentPadding={0}
        isOpen={!!videoId}
        onRequestClose={() => setVideoId('')}
        mobile={false}
      >
        <div className="embed-responsive embed-responsive--16x9">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&amp;showinfo=0&amp;autoplay=1&amp;color=white`}
            allowFullScreen
            title={videoId}
          />
        </div>
      </Modal>
    </>
  );
};

PartnerLanding.getInitialProps = context => {
  // Would've nicer if we had energiebespaarders.nl/partner/:partner
  // but we can't do that for backwards compatibility: every partner is on the root
  // Update: But we can use redirects, already doing this for installer routes

  const pathname = getPathnameFromAsPath(context.asPath || '');

  const partnerKey = partnerLandingRoutes.find(route => route.path === pathname)?.partner;
  const partner = DEB_PARTNERS.find(partner => partner.key === partnerKey);

  if (partner) return { partnerKey };

  if (context.res) {
    // server
    console.error('Partner route has no partner partner', context.asPath, pathname);
    context.res.statusCode = 404;
  } else {
    // client
    void Router.push('/');
  }
  return {};
};

export default PartnerLanding;
